import React from 'react';
import PropTypes from 'prop-types';
import { defineMessages, FormattedMessage } from 'react-intl';
import { Link } from 'react-router-dom';
import get from 'lodash/get';

import TextLink from '@redbubble/design-system/react/TextLink';
import * as constants from '@redbubble/design-system/react/constants';
import ChevronRightIcon from '@redbubble/design-system/react/Icons/ChevronRight';
import Box from '@redbubble/design-system/react/Box';

import FavoritableItemCarousel, {
  fragments as carouselFragments
} from '../FavoritableItemCarousel';
import CarouselHeading from '../CarouselHeading';
import { useWorkRecommendations } from '../../containers/apollo/withWorkRecommendations';
import { fragments as favoriteFragments } from '../../containers/apollo/withFavoriteStatuses';
import { fragments as feedGridFragments } from '../Feed/FeedGrid';
import { getUrlWithLocale } from '../../lib/routing';
import {
  analyticsPayload,
  EXPLORE_FEED_PREVIEW_SOURCE
} from '../../lib/analytics';

import styles from './RecommendedWorksPreviewCarousel.css';
import { AnalyticsItemsList } from '../../lib/gtm';

const messages = defineMessages({
  feedTitle: {
    defaultMessage: 'Designs picked for you',
    description: 'Heading for the feed of work recommendations.'
  },
  seeMore: {
    defaultMessage: 'See more',
    description: 'Button for viewing the entire explore feed.'
  }
});

const RecommendedWorksPreviewCarousel = ({
  userInfo,
  profile,
  browser,
  logEvent,
  logComponentInView,
  addImpression,
  urlRef
}) => {
  const { isLoggedIn } = userInfo || {};
  const { works, loading } = useWorkRecommendations({
    limit: 24,
    userInfo,
    fragments: [
      ...carouselFragments,
      ...favoriteFragments,
      // include fragments from FeedGrid so the data is ready for page transition
      ...feedGridFragments
    ]
  });

  const locale = get(userInfo, 'locale', 'en');

  return (
    <Box marginBottom={2}>
      <FormattedMessage {...messages.feedTitle}>
        {titleText => (
          <FormattedMessage {...messages.seeMore}>
            {seeMoreText => (
              <CarouselHeading
                actionSlot={
                  <TextLink block intent={constants.PRIMARY}>
                    {props => (
                      <span className={styles.viewAll}>
                        <Link
                          {...props}
                          to={getUrlWithLocale(
                            locale,
                            `/explore/feed${
                              urlRef
                                ? `?ref=${EXPLORE_FEED_PREVIEW_SOURCE}`
                                : ''
                            }`
                          )}
                        >
                          {seeMoreText}
                          <ChevronRightIcon
                            size={constants.LARGE}
                            color="#FF596F"
                          />
                        </Link>
                      </span>
                    )}
                  </TextLink>
                }
              >
                {titleText}
              </CarouselHeading>
            )}
          </FormattedMessage>
        )}
      </FormattedMessage>
      <FavoritableItemCarousel
        loading={loading}
        items={works}
        rows={2}
        profile={profile}
        isLoggedIn={isLoggedIn}
        transparentCards
        showProductCounts={false}
        slideWidth={browser.is.large ? 220 : 150}
        expectedCount={browser.is.large ? 12 : 6}
        locale={locale}
        onFavorite={(_, newState, index) => {
          logEvent({
            analytics: analyticsPayload.favorite({
              newState,
              rank: index + 1,
              source: EXPLORE_FEED_PREVIEW_SOURCE
            })
          });
        }}
        urlRef={urlRef}
        onLoadingShowed={logComponentInView(EXPLORE_FEED_PREVIEW_SOURCE, true)}
        onContentShowed={logComponentInView(EXPLORE_FEED_PREVIEW_SOURCE, false)}
        logImpression={({ workId }, index) => {
          const impression = analyticsPayload.impression(
            EXPLORE_FEED_PREVIEW_SOURCE,
            workId,
            index + 1
          );
          addImpression(impression);
        }}
        analyticsComponentID={
          AnalyticsItemsList.RecommendedWorksPreviewCarousel
        }
      />
    </Box>
  );
};

RecommendedWorksPreviewCarousel.propTypes = {
  browser: PropTypes.shape({
    is: PropTypes.shape({
      large: PropTypes.bool.isRequired
    }).isRequired
  }).isRequired,
  logEvent: PropTypes.func
};

RecommendedWorksPreviewCarousel.defaultProps = {
  logEvent: () => {}
};

export default RecommendedWorksPreviewCarousel;
